function isValidPrice(price) {
  return (
    price !== null &&
    price !== undefined &&
    price !== "" &&
    !isNaN(parseFloat(price))
  );
}

// Check if the class is already defined
if (typeof window.BundleManager === "undefined") {
  class BundleManager {
    static #bundles = {};

    static initializeProduct(bundleId) {
      if (!this.#bundles[bundleId]) {
        this.#bundles[bundleId] = {
          bundleId,
          variants: [],
        };
      }
    }

    static #validateBundleId(bundleId, methodName, returnValue = null) {
      if (!bundleId) {
        console.error(`${methodName}: Bundle ID is required`);
        return returnValue;
      }
    }

    static #validateVariantData(variantData, methodName, returnValue = null) {
      if (!variantData || !variantData.productId) {
        console.error(`${methodName}: Variant data is required`);
        return returnValue;
      }
    }

    static addVariant(bundleId, variantData) {
      this.#validateBundleId(bundleId, "ADD_VARIANT");
      this.#validateVariantData(variantData, "ADD_VARIANT");

      this.initializeProduct(bundleId);
      const bundle = this.#bundles[bundleId];
      const existingVariantIndex = bundle.variants.findIndex(
        (item) => parseInt(item.productId) === parseInt(variantData.productId),
      );

      if (existingVariantIndex !== -1) {
        bundle.variants[existingVariantIndex] = {
          productId: variantData.productId,
          ...variantData,
        };
      } else {
        bundle.variants.push({
          productId: variantData.productId,
          ...variantData,
        });
      }

      console.log("Bundle after update:", this.#bundles[bundleId]);
    }

    static addDefaultVariant(bundleId, variantData) {
      this.#validateBundleId(bundleId, "ADD_DEFAULT_VARIANT");
      this.#validateVariantData(variantData, "ADD_DEFAULT_VARIANT");

      this.initializeProduct(bundleId);
      const bundle = this.#bundles[bundleId];

      const existingVariant = bundle.variants.find(
        (item) => parseInt(item.productId) === parseInt(variantData.productId),
      );

      if (!existingVariant) {
        bundle.variants.push({
          productId: variantData.productId,
          ...variantData,
        });
      }
    }

    static getBundle(bundleId) {
      this.#validateBundleId(bundleId, "GET_BUNDLE", []);
      return this.#bundles[bundleId]?.variants || [];
    }

    static getBundleCount(bundleId) {
      this.#validateBundleId(bundleId, "GET_BUNDLE_COUNT", 0);
      return this.getBundle(bundleId).length;
    }

    static clearBundle(bundleId) {
      if (bundleId) {
        delete this.#bundles[bundleId];
      } else {
        this.#bundles = {};
      }
    }
  }

  // Assign the class to a global variable
  window.BundleManager = BundleManager;
}

if (!customElements.get("bundle-product-form")) {
  customElements.define(
    "bundle-product-form",
    class BundleProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector("form");
        this.mainProductId = this.form.dataset.productid;
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.isBundleKit =
          !!this.form.dataset.isBundleKit &&
          this.form.dataset.isBundleKit === "true";
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this?.querySelector('[type="submit"]');

        if (document.querySelector("cart-drawer") && this.submitButton)
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        // Disable the submit button initially
        if (this.submitButton) {
          this.submitButton.setAttribute("aria-disabled", "true");
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (
          this.submitButton.getAttribute("aria-disabled") === "true" ||
          BundleManager.getBundleCount(this.mainProductId) === 0
        )
          return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading-overlay__spinner").classList.remove(
          "hidden",
        );

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        if (this.isBundleKit) {
          formData.append("quantity", "1");
        }
        const formObject = Object.fromEntries(formData.entries());
        formObject.properties = this.prepareBundleItems(
          formObject.quantity,
          this.mainProductId,
        );

        fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formObject),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
            window.loyaltylion && window.loyaltylion?.ui?.refresh();
            let installments = response.final_line_price / 4;
            if (typeof window.addKlarnaToMiniCart === "function")
              addKlarnaToMiniCart(installments);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading-overlay__spinner").classList.add(
              "hidden",
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      prepareBundleItems(quantity, mainProductId) {
        const items = {
          _variants: [],
          _bundleId: Math.floor(Math.random() * 1000000000000),
        };

        const bundleVariants = BundleManager.getBundle(mainProductId);
        bundleVariants.forEach((variant, index) => {
          items[index + 1] = `\n${quantity} x ${variant.name} [${variant.sku}]`;
          items._variants.push({
            id: String(variant.id),
            price: isValidPrice(variant.price)
              ? parseFloat((variant.price / 100).toFixed(2))
              : 0,
          });
        });

        window.PDPBundle = { ...items };
        items._variants = JSON.stringify(items._variants);

        return items;
      }
    },
  );
}

if (!customElements.get("color-select")) {
  customElements.define(
    "color-select",
    class ColorSelect extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.initializeColorSelect();
      }

      initializeColorSelect() {
        this.bundleProductId = this.dataset.bundleProductId;
        this.mainProductId = this.dataset.mainProductId;
        this.hasOnlyDefaultVariant =
          this.dataset.hasOnlyDefaultVariant === "true";

        this.hasOnlyDefaultVariant
          ? this.updateHasOnlyOption()
          : this.setupVariantSelection();
      }

      setupVariantSelection() {
        this.detailsToggle = this.querySelector("details");
        if (!this.detailsToggle) return;
        this.summaryToggle = this.detailsToggle.querySelector("summary");
        this.dropDownMenu = this.detailsToggle.querySelector("ul");
        if (!this.dropDownMenu || !this.summaryToggle) return;
        this.addEventListeners();
      }

      addEventListeners() {
        this.detailsToggle.addEventListener(
          "focusout",
          this.handleFocusOut.bind(this),
        );
        this.summaryToggle.addEventListener(
          "click",
          this.handleToggle.bind(this),
        );
        this.dropDownMenu.addEventListener(
          "click",
          this.handleSelect.bind(this),
        );
      }

      handleFocusOut() {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) {
            this.closeDropDown();
          }
        });
      }

      handleToggle(event) {
        const isExpanded = this.detailsToggle.hasAttribute("open");
        event.currentTarget.setAttribute("aria-expanded", !isExpanded);
      }

      closeDropDown() {
        this.detailsToggle.removeAttribute("open");
        this.summaryToggle.setAttribute("aria-expanded", false);
      }

      handleSelect(event) {
        const target = event.target?.closest("button");

        if (!target) return;

        const currentVariant = this.getVariantData(
          target.dataset.optionValueId,
        );
        if (!currentVariant) return;

        this.updateBundleProductImage(currentVariant.featured_image?.src);
        this.updateSelectedOption(target);
        this.closeDropDown();
        this.updateSelectedVariantsArray(currentVariant);
      }

      updateBundleProductImage(imageSrc) {
        if (!imageSrc) return;
        const currentImages = document.querySelectorAll(
          `img[data-bundle-product-image="${this.bundleProductId}"]`,
        );
        if (!currentImages?.length) return;
        currentImages.forEach((image) => {
          if (!image) return;
          image.src = `${imageSrc}&width=200`;
        });
      }

      updateSelectedOption(target) {
        const { optionText, optionStyle } = target.dataset;
        this.dropDownMenu
          .querySelectorAll("button")
          .forEach((button) => button.classList.remove("active"));
        target.classList.add("active");

        this.summaryToggle.querySelector(
          "[data-selected-option-text]",
        ).textContent = optionText;
        this.summaryToggle.querySelector(
          "[data-selected-option-style]",
        ).style.cssText = optionStyle;
      }

      updateHasOnlyOption() {
        const currentVariant = this.getVariantData(this.bundleProductId);
        if (!currentVariant) return;

        BundleManager.addDefaultVariant(this.mainProductId, {
          productId: this.bundleProductId,
          ...currentVariant,
        });
      }

      updateSelectedVariantsArray(currentVariant) {
        BundleManager.addVariant(this.mainProductId, {
          productId: this.bundleProductId,
          ...currentVariant,
        });

        if (
          Number(this.dataset.bundleLength) ===
          BundleManager.getBundleCount(this.mainProductId)
        ) {
          this.enableSubmitButton(this.mainProductId);
        }
      }

      enableSubmitButton(mainProductId) {
        const bundleProductForms = document.querySelectorAll(
          `[data-bundle-product-form="${mainProductId}"]`,
        );
        if (!bundleProductForms?.length) return;
        bundleProductForms.forEach((form) => {
          if (form.submitButton) {
            form.submitButton.setAttribute("aria-disabled", false);
          }
        });
      }

      getVariantData(inputId) {
        const variantDataElement = this.querySelector(
          `script[type="application/json"][data-resource="${inputId}"]`,
        );
        return variantDataElement
          ? JSON.parse(variantDataElement.textContent)
          : null;
      }
    },
  );
}

// Add product variant to bundle when quick add modal is opened
(() => {
  document.addEventListener("DOMContentLoaded", () => {
    const bundleProductList = document.querySelector(".bundle-product__list");
    if (!bundleProductList) return;

    const bundleModals = bundleProductList.querySelectorAll(
      "[data-bundle-modal]",
    );
    if (bundleModals.length === 0) return;

    const getColorSelect = (quickViewId) =>
      document.querySelector(
        `color-select[data-quick-view-id="${quickViewId}"]`,
      );

    const handleProductFormSubmit = (colorSelect, productForm, closeButton) => {
      if (!colorSelect || !productForm) return;

      const productFormSubmit = productForm.querySelector(
        "button[type='button'][name='add']",
      );
      if (!productFormSubmit) return;

      productFormSubmit.addEventListener("click", (e) => {
        e.preventDefault();
        const loadingSpinner = productFormSubmit.querySelector(
          ".loading-overlay__spinner",
        );

        productFormSubmit.setAttribute("aria-disabled", true);
        productFormSubmit.classList.add("loading");
        loadingSpinner.classList.remove("hidden");

        const variantId = productForm.querySelector(
          "input[type='hidden'][name='id']",
        )?.value;
        if (!variantId) return;

        const selectedOption = colorSelect.querySelector(
          `[data-option-id="${variantId}"]`,
        );
        if (!selectedOption) return;

        // Add a fake delay of 1.5 seconds (1500 milliseconds)
        colorSelect.handleSelect({ target: selectedOption });
        setTimeout(() => {
          closeButton.click();
          productFormSubmit.setAttribute("aria-disabled", false);
          productFormSubmit.classList.remove("loading");
          loadingSpinner.classList.add("hidden");
        }, 1500);
      });
    };

    bundleModals.forEach((modal) => {
      modal.addEventListener("click", () => {
        const modalId = modal.id;
        if (!modalId) return;

        const colorSelect = getColorSelect(modalId);
        if (!colorSelect) return;

        document.addEventListener(
          "quick-add:loaded",
          () => {
            const productForm = document.querySelector(
              "[data-quick-product-form]",
            );
            const closeButton = document.querySelector(
              `#ModalClose-${modal.dataset.bundleModal}`,
            );
            if (!productForm || !closeButton) return;

            handleProductFormSubmit(colorSelect, productForm, closeButton);
          },
          { once: true },
        );
      });
    });
  });
})();
